/* istanbul ignore file */
import React, {
  useState,
  useMemo,
  useRef,
  useEffect,
  ReactElement,
} from 'react'
import classnames from 'classnames/bind'
import {
  APIProvider,
  Map as GoogleMap,
  AdvancedMarker,
  AdvancedMarkerAnchorPoint,
  ControlPosition,
  useMap,
} from '@vis.gl/react-google-maps'
import configment from '@adc/configment'
import styles from './styles.scss'
import Icon from '../../elementals/icon'
// @ts-ignore next
const cx = classnames.bind(styles)

const GOOGLE_MAPS_LIBRARIES = ['places']
const US_CENTER_LAT_LNG = {
  lat: 39.8097343,
  lng: -98.5556199,
}
const DEFAULT_ZOOM_LEVEL = 18

// Back-compat shim: the legacy createMapOptions(maps) callback referenced
// `maps.ControlPosition`. Expose vis.gl's ControlPosition enum so existing
// consumer callbacks keep working without change.
type MapsShim = { ControlPosition: typeof ControlPosition }

type SimpleMapProps = {
  defaultView?: ReactElement
  coords?: { lat: number; lng: number }
  createMapOptions?: (maps: MapsShim) => google.maps.MapOptions
  onMapClick?: (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => void
  showStreetView?: boolean
}

/**
 * Sets up the Street View panorama once the map instance is ready. Replaces the
 * legacy google-map-react onGoogleApiLoaded({ map, maps }) callback, which under
 * React 18 detached from the concurrent hydrateRoot tree. useMap() returns the
 * live google.maps.Map only after the Maps JS API has loaded.
 */
const StreetViewBridge: React.FC<{
  coords?: { lat: number; lng: number }
  onUnavailable: () => void
}> = ({ coords, onUnavailable }) => {
  const map = useMap()

  useEffect(() => {
    if (!map || !coords) return

    const streetView = new google.maps.StreetViewPanorama(
      document.getElementById('street-view') as HTMLElement,
      {
        position: coords,
        pov: { heading: 270, pitch: 0 },
        zoom: 1,
        disableDefaultUI: true,
        clickToGo: false,
      }
    )
    map.setStreetView(streetView)

    const sv = new google.maps.StreetViewService()
    sv.getPanorama(
      { location: { lat: coords.lat, lng: coords.lng }, radius: 50 },
      (_data, status) => {
        if (status !== google.maps.StreetViewStatus.OK) onUnavailable()
      }
    )
  }, [map, coords, onUnavailable])

  return null
}

const SimpleMap: React.FC<SimpleMapProps> = ({
  defaultView,
  coords,
  createMapOptions = () => ({}),
  onMapClick = () => undefined,
  showStreetView = false,
}: SimpleMapProps) => {
  const [streetViewAvailable, setStreetViewAvailable] = useState(true)
  const streetViewRef = useRef<HTMLDivElement | null>(null)

  const apiKey = configment.get('GOOGLE_MAPS_API_KEY', '')

  const mapOptions = useMemo<google.maps.MapOptions>(() => {
    return {
      mapTypeControlOptions: {
        position: ControlPosition.TOP_RIGHT,
      },
      mapTypeControl: true,
      fullscreenControl: false,
      streetViewControl: false,
      clickableIcons: false,
      zoomControl: false,
      gestureHandling: 'none',
      draggableCursor: 'pointer',
      ...createMapOptions({ ControlPosition }),
    }
  }, [createMapOptions])

  if (showStreetView && !streetViewAvailable && defaultView) return defaultView

  // Never mount APIProvider without a key: an empty-key script load poisons the
  // singleton Maps script for the whole page (all other maps then fail auth).
  if (!apiKey) return defaultView ?? null

  return (
    <div className={cx('map-container')}>
      <APIProvider
        apiKey={apiKey}
        libraries={GOOGLE_MAPS_LIBRARIES}
        language='en'
        version='weekly'
      >
        <GoogleMap
          mapId={configment.get('GOOGLE_MAPS_MAP_ID', 'DEMO_MAP_ID')}
          defaultCenter={coords ? { ...coords } : US_CENTER_LAT_LNG}
          defaultZoom={DEFAULT_ZOOM_LEVEL}
          onClick={onMapClick as unknown as () => void}
          style={{ width: '100%', height: '100%' }}
          {...(mapOptions as React.ComponentProps<typeof GoogleMap>)}
        >
          {coords && (
            <AdvancedMarker
              position={coords}
              anchorPoint={AdvancedMarkerAnchorPoint.BOTTOM_CENTER}
            >
              <Marker onClick={onMapClick} lat={coords.lat} lng={coords.lng} />
            </AdvancedMarker>
          )}
          {showStreetView && (
            <StreetViewBridge
              coords={coords}
              onUnavailable={() => setStreetViewAvailable(false)}
            />
          )}
        </GoogleMap>
      </APIProvider>
      {/* Street View */}
      {showStreetView && (
        <div
          id='street-view'
          // @ts-ignore next
          onClick={e => {
            const target = e.target as HTMLElement
            //Do not propagate click event if the click is on the street view controls
            if (target.closest('.gm-control-active')) {
              e.stopPropagation()
            } else {
              onMapClick(
                e as unknown as React.MouseEvent<HTMLDivElement, MouseEvent>
              )
            }
          }}
          ref={streetViewRef}
          className={cx('street-view', 'pointer')}
        />
      )}
    </div>
  )
}

type MarkerProps = {
  lat: number
  lng: number
  onClick: (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => void
}
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const Marker = (_props: MarkerProps) => {
  const { onClick } = _props
  return (
    <div onClick={onClick} className={cx('marker-container')}>
      <Icon
        name='fa-location-dot'
        iconStyle='solid'
        size='huge'
        color='error'
        className={cx('marker')}
      />
    </div>
  )
}

export default SimpleMap
